import { Component, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { BreadcrumbService } from '../../shared/components/breadcrumbs/breadcrumbs';
import { Setting } from '../../shared/models/setting.model';
import { NotificationService } from '../../shared/services/notification.service';
import { SettingService } from '../../shared/services/setting.service';
import { UsersService } from '../../shared/services/users.service';

@Component({
  standalone: false,
  selector: 'setting-index-component',
  templateUrl: './setting-index.component.html',
  styleUrls: ['./setting-index.component.scss'],
})
export class SettingIndexComponent implements OnInit {
  models: { model: Setting; val: string | number | boolean; spinner: boolean }[] = [];
  groupedModels: Record<string, any[]> = {};
  sortedGroupKeys: string[] = [];
  objectKeys = Object.keys;
  isAdmin: boolean = null;

  loading = false;

  names = {
    mtc_disabled: 'Manual testing disabled',
    mtc_disabled_for_client: 'Manual testing disabled (for clients)',
    mtc_disabled_message: 'Manual testing disabled message',
    ntc_disabled: 'Live testing disabled',
    ntc_disabled_for_client: 'Live testing disabled (for clients)',
    ntc_disabled_message: 'Live testing disabled message',
    motc_disabled: 'MO testing disabled',
    motc_disabled_for_client: 'MO testing disabled (for clients)',
    motc_disabled_message: 'MO testing disabled message',
    voice_disabled: 'All CLI testing disabled',
    voice_disabled_for_client: 'All CLI testing disabled (for clients)',
    voice_disabled_message: 'All CLI testing disabled message',
    sip_disabled: 'SIP CLI testing disabled',
    sip_disabled_for_client: 'SIP CLI testing disabled (for clients)',
    sip_disabled_message: 'SIP CLI testing disabled message',
    api_disabled: 'Public API testing disabled',
    api_disabled_for_client: 'Public API testing disabled (for clients)',
    api_disabled_message: 'Public API testing disabled message',
  };

  constructor(
    public breadcrumbs: BreadcrumbService,
    public service: SettingService,
    public router: Router,
    public notificationService: NotificationService,
    titleService: Title,
    public userService: UsersService
  ) {
    titleService.setTitle('Settings');
  }

  ngOnInit() {
    this.userService
      .can('admin')
      .then(_ => (this.isAdmin = _))
      .catch(e => {});
    this.breadcrumbs.root('Dashboard', ['dashboard']).add('Settings', ['settings'], true);
    this.update();
  }

  update() {
    this.loading = true;
    this.service.all().subscribe(settings => {
      this.models = settings.map(_ => {
        return { model: _, val: this.service.toValue(_), spinner: false, type: this.getType(_) };
      });

      this.groupedModels = this.groupByType(this.models);
      this.sortedGroupKeys = this.sortGroupKeys(this.groupedModels);
      this.loading = false;
    });
  }

  getType(model) {
    if (model.name.startsWith('ntc')) return 'Live number testing';
    if (model.name.startsWith('motc')) return 'MO testing';
    if (model.name.startsWith('mtc')) return 'Manual testing';
    if (model.name.startsWith('voice')) return 'All CLI testing';
    if (model.name.startsWith('sip')) return 'SIP CLI testing';
    if (model.name.startsWith('api')) return 'Public API testing';
  }

  private sortGroupKeys(groupedModels: Record<string, any[]>): string[] {
    return ['Live number testing', 'Manual testing', 'MO testing', 'All CLI testing', 'SIP CLI testing', 'Public API testing'].filter(
      key => key in groupedModels
    ); // Make sure the name follows the type returned by getType() ...
  }

  private groupByType(models: any[]): Record<string, any[]> {
    return models.reduce(
      (acc, model) => {
        const key = model.type;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(model);
        return acc;
      },
      {} as Record<string, any[]>
    );
  }

  onSettingChange(val, model) {
    if (val instanceof Event) {
      return;
    }
    model.model.valRaw = this.service.toString(val, model.model.valType);
    model.spinner = true;

    this.service.save(model.model).subscribe(
      _ => {
        // this.service.updateSettings();
        model.spinner = false;
        this.notificationService.success('Setting "' + this.getTitle(model.model.name) + '" updated', 'Settings');
      },
      error => {
        this.loading = false;
        this.notificationService.error({
          title: 'Settings',
          message: 'An error occurred while updating the setting',
          serviceName: 'MTC',
          requestMessage: error.statusText,
          requestCode: error.status,
          ts: error.timestamp ? error.timestamp : null,
        });
      }
    );
  }

  isAnySpinnerActive(): boolean {
    return Object.values(this.groupedModels).some(group => group.some(model => model.spinner));
  }

  getTitle(name) {
    if (typeof this.names[name] !== 'undefined') {
      return this.names[name];
    }

    return name;
  }
}
